import { Capacitor } from '@capacitor/core';
import type { Session } from '@supabase/supabase-js';
import { FirebaseMessaging } from '@/lib/notifications/firebase-messaging';

/**
 * Enregistrement AUTOMATIQUE du téléphone pour les notifications push (app
 * iOS/Android installée). Avant, un appareil n'était enregistré que si
 * l'utilisateur allait activer « Notifications push » dans Mon profil — la
 * plupart ne le faisaient jamais et ne recevaient donc rien (rendez-vous,
 * messages, devoirs…).
 *
 * Désormais, à chaque ouverture de session :
 *  - permission déjà accordée → on renvoie le token au serveur (rattache
 *    l'appareil au compte CONNECTÉ, même après un changement de compte) ;
 *  - permission jamais demandée → on la demande UNE fois par appareil ;
 *  - permission refusée → rien (l'utilisateur peut la rouvrir dans ses réglages).
 * À la déconnexion, le token FCM est supprimé : l'appareil cesse de recevoir
 * les notifications de l'ancien compte (le serveur purge le token mort au
 * prochain envoi, `UNREGISTERED`).
 *
 * Le bouton de Mon profil reste l'interrupteur manuel (`usePushNotifications`).
 */

export type NativePushAction = 'register' | 'ask' | 'none';

/** Décision PURE (testée) selon la permission OS et si on a déjà demandé. */
export function nativePushAction(permission: string, alreadyAsked: boolean): NativePushAction {
  if (permission === 'granted') return 'register';
  if (permission === 'denied') return 'none';
  // 'prompt' / 'prompt-with-rationale' : jamais tranché par l'utilisateur.
  return alreadyAsked ? 'none' : 'ask';
}

const ASKED_KEY = 'jr_push_asked';
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;

function readAsked(): boolean {
  try {
    return localStorage.getItem(ASKED_KEY) === '1';
  } catch {
    return false;
  }
}

function markAsked(): void {
  try {
    localStorage.setItem(ASKED_KEY, '1');
  } catch {
    /* stockage indisponible : on redemandera, sans gravité */
  }
}

async function postToken(accessToken: string, token: string): Promise<void> {
  const res = await fetch(`${SUPABASE_URL}/functions/v1/push-subscribe`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
    body: JSON.stringify({ platform: Capacitor.getPlatform(), token }),
  });
  if (!res.ok) throw new Error(`push-subscribe ${res.status}`);
}

let lastSyncedUserId: string | null = null;
/** Dernier jeton réellement envoyé au serveur — évite de le repousser pour rien. */
let lastSentToken: string | null = null;

/**
 * Appelé à chaque ouverture/restauration de session, et à chaque retour de
 * l'app au premier plan (`force`). Ne lève jamais.
 *
 * ⚠️ Le `force` est indispensable : Firebase RÉVOQUE un jeton (réinstallation,
 * restauration de sauvegarde, `deleteToken()` à la déconnexion, expiration).
 * Sans revérification, le serveur garde un jeton mort pour toujours et
 * l'appareil ne reçoit plus rien — c'est ce qui s'était passé (tous les jetons
 * iOS répondaient `UNREGISTERED` côté Firebase).
 */
export async function syncNativePush(session: Session, options: { force?: boolean } = {}) {
  if (!Capacitor.isNativePlatform()) return;
  // Une seule synchro par compte et par lancement (les rafraîchissements de
  // jeton de session ne relancent rien) — sauf retour au premier plan.
  if (!options.force && lastSyncedUserId === session.user.id) return;
  lastSyncedUserId = session.user.id;
  try {
    const { receive } = await FirebaseMessaging.checkPermissions();
    const action = nativePushAction(receive, readAsked());
    if (action === 'none') return;
    if (action === 'ask') {
      // On ne redemande jamais la permission sur un simple retour au premier
      // plan : la demande appartient à l'ouverture de session.
      if (options.force) return;
      markAsked();
      const perm = await FirebaseMessaging.requestPermissions();
      if (perm.receive !== 'granted') return;
    }
    const { token } = await FirebaseMessaging.getToken();
    if (!token) return;
    if (options.force && token === lastSentToken) return; // rien n'a changé
    await postToken(session.access_token, token);
    lastSentToken = token;
    console.log('[push] appareil enregistré', options.force ? '(retour au premier plan)' : '');
  } catch (e) {
    lastSyncedUserId = null; // retentera à la prochaine session
    console.error('[push] enregistrement automatique échoué', e);
  }
}

/**
 * Écoute GLOBALE de la rotation du jeton FCM. À poser une seule fois au
 * démarrage : avant, cet écouteur ne vivait que dans `usePushNotifications`,
 * donc seulement pendant que « Mon profil » était à l'écran — un jeton qui
 * tournait ailleurs n'était jamais renvoyé au serveur.
 *
 * `getAccessToken` est injecté (pas d'import de `supabase` ici : ce module est
 * aussi chargé par des tests purs).
 */
export function watchNativePushToken(getAccessToken: () => Promise<string | null>): void {
  if (!Capacitor.isNativePlatform()) return;
  void FirebaseMessaging.addListener('tokenReceived', ({ token }) => {
    if (!token || token === lastSentToken) return;
    void (async () => {
      try {
        const accessToken = await getAccessToken();
        if (!accessToken) return; // personne de connecté : rien à rattacher
        await postToken(accessToken, token);
        lastSentToken = token;
        console.log('[push] nouveau jeton enregistré (rotation)');
      } catch (e) {
        console.error('[push] rotation du jeton : enregistrement échoué', e);
      }
    })();
  }).catch((e: unknown) => {
    console.error('[push] écoute de la rotation du jeton impossible', e);
  });
}

/** Appelé à la déconnexion : l'appareil ne doit plus recevoir les notifs de l'ancien compte. */
export async function detachNativePush(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;
  lastSyncedUserId = null;
  lastSentToken = null;
  try {
    await FirebaseMessaging.deleteToken();
  } catch (e) {
    console.error('[push] suppression du token échouée', e);
  }
}
